// @section 1 DailyOpsReview route — standalone Daily Ops Review page.
// Created 2026-06-06 when the briefing moved off My Day so Adam leads the
// morning with Alerts + Calendar. Fetches the same /api/hq/my-day aggregate
// MyDay uses, then renders the briefing via the shared BriefingCard.

import { useEffect, useState } from 'react';
import { getMyDay } from '../api';
import { useMeUser } from '../lib/useMe';
import { BriefingCard, type Briefing } from '../components/BriefingCard';

// @subsection 1.1 Component

export default function DailyOpsReview({ embedded = false }: { embedded?: boolean } = {}) {
  const me = useMeUser();
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    getMyDay(me.userId)
      .then((hq) => {
        setBriefing(hq?.latest_briefing ?? null);
        setErr(null);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [me]);

  return (
    <div className={embedded ? 'max-w-4xl' : 'px-4 py-5 md:px-7 md:py-6 max-w-4xl h-full overflow-y-auto'}>
      {!embedded && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-1">Atlas / Daily Ops Review</div>
          <h1 className="text-3xl font-bold text-paper-100">Daily Ops Review</h1>
          <p className="text-sm text-muted mt-1">
            Published every morning by the 6AM scheduled session. Pipeline status, opportunities, and ops drift.
          </p>
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 animate-pulse">
          <div className="h-4 w-40 bg-ink-800 rounded mb-3" />
          <div className="h-6 w-3/4 bg-ink-800/60 rounded mb-2" />
          <div className="h-3 w-full bg-ink-800/40 rounded" />
        </div>
      )}

      {!loading && err && (
        <div className="rounded-xl border border-stall/40 bg-stall/10 p-5 text-sm text-stall">
          Failed to load briefing: {err}
        </div>
      )}

      {!loading && !err && !briefing && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-8 text-center">
          <div className="text-paper-100 font-semibold mb-1">No briefing for today yet</div>
          <p className="text-sm text-muted">
            The 6AM scheduled session publishes the daily review to <code className="text-paper-100/70">hq_core.briefings</code>.
            Check back after the next run.
          </p>
        </div>
      )}

      {!loading && !err && briefing && <BriefingCard b={briefing} />}
    </div>
  );
}
